import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FiRefreshCw } from 'react-icons/fi';
import InvoicePageShell from './components/InvoicePageShell';
import Snackbar from '../Admin-Subscriptions/components/Snackbar';
import { subscriptionAdminApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import { formatInr } from '../subscriptionAdmin/razorpay';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function statusColor(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'issued' || normalized === 'paid') return 'success';
  if (normalized === 'draft' || normalized === 'pending') return 'warning';
  if (normalized === 'cancelled' || normalized === 'void') return 'default';
  return 'default';
}

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canIssue = canWrite('invoices');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await subscriptionAdminApi.listInvoices();
      setInvoices(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (event, id) => {
    event.stopPropagation();
    try {
      await subscriptionAdminApi.issueInvoice(id);
      setSnackbar({ open: true, type: 'success', message: 'Invoice issued.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  return (
    <InvoicePageShell
      title="View Invoice"
      subtitle="Browse and manage invoices for paid billing cycles."
      backTo="/invoice"
      actions={
        <Button
          variant="outlined"
          size="small"
          startIcon={<FiRefreshCw />}
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/invoice/${inv._id}`)}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {inv.invoiceNumber || String(inv._id).slice(-8)}
                  </TableCell>
                  <TableCell>
                    <Chip label={inv.status || '—'} size="small" color={statusColor(inv.status)} />
                  </TableCell>
                  <TableCell>{formatInr(inv.totalAmount ?? inv.amount)}</TableCell>
                  <TableCell>
                    {inv.customerName || inv.customerEmail || String(inv.userId || '').slice(-8) || '—'}
                  </TableCell>
                  <TableCell>{formatDate(inv.issuedAt || inv.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/invoice/${inv._id}`);
                      }}
                    >
                      View
                    </Button>
                    {canIssue && inv.status !== 'issued' && (
                      <Button size="small" color="primary" onClick={(event) => issue(event, inv._id)}>
                        Issue
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!invoices.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No invoices yet. Invoices are created when billing cycles are paid.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </InvoicePageShell>
  );
}
